import React, { useState, useEffect } from "react";
import { Drawer } from "@/shedcnUI/drawer";
import api from "@/api/config";
import {
  FaSpinner,
  FaExclamationTriangle,
  FaCheckCircle,
  FaExclamationCircle,
  FaInfoCircle,
  FaTrashAlt,
} from "react-icons/fa";
import { Dialog, DialogContent, DialogTitle } from "@/shedcnUI/dialog";
import { toast } from "react-hot-toast";
interface TaskStatus {
  task_id: string;
  status: string;
  state: string;
  percentage: number;
  total_emails: number;
  sent_emails: number;
  failed_emails: number;
  error_message?: string;
  message?: string;
  started_at?: string;
  completed_at?: string;
  estimated_remaining?: string;
}

interface CampaignStatusDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  campaignName: string;
  campaignUuid?: string;
  taskId?: string;
  statusUrl?: string;
}

export const CampaignStatusDrawer: React.FC<CampaignStatusDrawerProps> = ({
  open = true,
  onOpenChange,
  campaignName,
  campaignUuid,
  taskId,
  statusUrl,
}) => {
  const [taskStatus, setTaskStatus] = useState<TaskStatus | null>(null);
  const [startTime, setStartTime] = useState<Date | null>(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [estimatedTime, setEstimatedTime] = useState<string>("Calculating...");
  const [progressHistory, setProgressHistory] = useState<
    { time: Date; percentage: number }[]
  >([]);
  const [isCancelling, setIsCancelling] = useState(false);

  const [notification, setNotification] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: "error" | "info" | "success";
  }>({
    isOpen: false,
    title: "",
    message: "",
    type: "info",
  });

  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: "",
    message: "",
    onConfirm: () => {},
  });

  const notify = (
    title: string,
    message: string,
    type: "error" | "info" | "success" = "info",
  ) => {
    setNotification({ isOpen: true, title, message, type });
  };

  // Set start time when opened
  useEffect(() => {
    if (open && !startTime) {
      setStartTime(new Date());
    } else if (!open) {
      // Reset when closed
      setTaskStatus(null);
      setStartTime(null);
      setElapsedTime(0);
      setProgressHistory([]);
      setEstimatedTime("Calculating...");
    }
  }, [open]);

  // Polling logic
  useEffect(() => {
    if (!open || !taskId) return;

    let isMounted = true;

    const fetchStatus = async () => {
      try {
        const response = await api.get(`/api/task/${taskId}/status`);
        const data = response.data;

        if (!isMounted) return true;

        let percentage =
          typeof data.percentage === "number"
            ? data.percentage
            : parseInt(data.percentage) || 0;
        percentage = Math.max(0, Math.min(100, percentage));

        if (percentage > 0) {
          setProgressHistory((prev) => {
            const lastPercentage =
              prev.length > 0 ? prev[prev.length - 1].percentage : 0;
            if (percentage > lastPercentage) {
              return [...prev, { time: new Date(), percentage }];
            }
            return prev;
          });
        }

        const newTaskStatus = {
          task_id: data.task_id || taskId,
          status: data.state || data.status || "processing",
          state: data.state || data.status || "processing",
          percentage,
          total_emails: data.total_emails || 0,
          sent_emails: data.processed || 0,
          failed_emails: data.failed || 0,
          error_message: data.error_message || data.error,
          message: data.message,
          started_at: data.started_at,
          completed_at: data.completed_at,
          estimated_remaining: data.estimated_remaining,
        };

        setTaskStatus(newTaskStatus);

        const taskState = data.state || data.status;
        if (
          taskState === "completed" ||
          taskState === "failed" ||
          taskState === "cancelled" ||
          taskState === "error"
        ) {
          toast(`Task ${taskState === "completed" ? "Completed" : "Failed"}`)
          return false; // Stop polling
        }
        return true;
      } catch (error) {
        toast.error("Error fetching task status.");
        console.error("Error fetching task status:", error);
        return true; // continue trying
      }
    };

    fetchStatus();

    const interval = setInterval(async () => {
      const shouldContinue = await fetchStatus();
      if (!shouldContinue && isMounted) {
        clearInterval(interval);
      }
    }, 2000);

    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [taskId, open]);

  // Elapsed Time & Estimated Time calculation
  useEffect(() => {
    if (!open || !startTime) return;

    const interval = setInterval(() => {
      const elapsed = Math.floor(
        (new Date().getTime() - startTime.getTime()) / 1000,
      );
      setElapsedTime(elapsed);

      if (progressHistory.length >= 2 && taskStatus) {
        const recentHistory = progressHistory.slice(-5);
        const oldestPoint = recentHistory[0];
        const newestPoint = recentHistory[recentHistory.length - 1];

        const progressDiff = newestPoint.percentage - oldestPoint.percentage;
        const timeDiff =
          (newestPoint.time.getTime() - oldestPoint.time.getTime()) / 1000;

        if (progressDiff > 0 && taskStatus.percentage < 100) {
          const remainingProgress = 100 - taskStatus.percentage;
          const ratePerSecond = progressDiff / timeDiff;
          const estimatedSeconds = remainingProgress / ratePerSecond;

          if (estimatedSeconds > 3600) {
            const hours = Math.floor(estimatedSeconds / 3600);
            const minutes = Math.floor((estimatedSeconds % 3600) / 60);
            setEstimatedTime(`~${hours}h ${minutes}m remaining`);
          } else if (estimatedSeconds > 60) {
            const minutes = Math.floor(estimatedSeconds / 60);
            const seconds = Math.floor(estimatedSeconds % 60);
            setEstimatedTime(`~${minutes}m ${seconds}s remaining`);
          } else {
            setEstimatedTime(`~${Math.floor(estimatedSeconds)}s remaining`);
          }
        }
      } else if (taskStatus?.percentage === 0 || !taskStatus) {
        setEstimatedTime("Starting...");
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [startTime, progressHistory, taskStatus, open]);

  const handleCancel = async () => {
    if (!taskId) return;

    setConfirmModal({
      isOpen: true,
      title: "Cancel Generation?",
      message:
        "Are you sure you want to cancel the email generation? This will stop the process and cannot be undone.",
      onConfirm: async () => {
        setConfirmModal((prev) => ({ ...prev, isOpen: false }));
        setIsCancelling(true);
        try {
          await api.post(`/api/task/${taskId}/cancel`);
          setTaskStatus((prev) =>
            prev
              ? { ...prev, state: "cancelling", status: "cancelling" }
              : null,
          );
        } catch (err) {
          console.error("Failed to cancel task:", err);
          notify(
            "Cancellation Failed",
            "We couldn't cancel the task at this moment. It may still be running.",
            "error",
          );
        } finally {
          setIsCancelling(false);
        }
      },
    });
  };

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}m ${secs}s`;
  };

  // SVG Semi-circular progress calculation
  const radius = 100;
  const strokeWidth = 30;
  const circumference = Math.PI * radius;
  const progress = taskStatus?.percentage || 0;
  const offset = circumference - (progress / 100) * circumference;

  const isCompleted = taskStatus?.state === "completed";
  const isFailed = taskStatus && ["error", "failed"].includes(taskStatus.state);
  const isCancelled = taskStatus?.state === "cancelled";
  const isProcessing =
    taskStatus &&
    ["processing", "running", "starting"].includes(taskStatus.state);

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      width="480px"
      className="rounded-none max-h-full p-2 bg-white shadow-2xl"
    >
      <div className="flex flex-col items-center px-6 pt-4 pb-12 h-full bg-white rounded-xl">
        {/* Title */}
        <h2 className="text-[22px] font-bold text-[#111111] text-center mb-10 w-full max-w-[300px] leading-snug">
          {campaignName || "Campaign Processing"}
        </h2>

        {/* Progress Arc */}
        <div className="relative w-[280px] h-[140px] flex items-end justify-center overflow-hidden mb-6">
          <svg
            className="absolute top-0 left-0 w-full h-[280px]"
            viewBox="0 0 260 260"
          >
            {/* Background Arc */}
            <path
              d="M 30,130 A 100,100 0 0,1 230,130"
              fill="none"
              stroke="#F3F4F6"
              strokeWidth={strokeWidth}
              strokeLinecap="butt"
            />
            {/* Progress Arc */}
            <path
              d="M 30,130 A 100,100 0 0,1 230,130"
              fill="none"
              stroke={
                isFailed ? "#EF4444" : isCompleted ? "#10B981" : "#006EFF"
              }
              strokeWidth={strokeWidth}
              strokeLinecap="butt"
              strokeDasharray={circumference}
              strokeDashoffset={offset}
              className="transition-all duration-1000 ease-out"
            />
          </svg>
          <div className="flex flex-col items-center absolute bottom-0">
            <span className="pt-7 text-[48px] font-bold leading-none text-[#111111]">
              {progress}
            </span>
            <span className="text-[14px] font-medium text-gray-500 mt-1">
              out of 100%
            </span>
          </div>
        </div>

        <div className="text-center text-[13px] font-medium text-gray-600 mb-6 min-h-[20px]">
          {isCompleted
            ? "Successfully Completed"
            : isFailed
              ? taskStatus?.error_message || "An error occurred"
              : isCancelled
                ? "Task Cancelled"
                : taskStatus?.message || "Initializing..."}
        </div>

        {/* Action Button */}
        {isProcessing && (
          <button
            onClick={handleCancel}
            disabled={isCancelling}
            className="flex items-center justify-center gap-2 bg-[#006EFF] text-white px-6 py-2.5 rounded-[8px] font-semibold text-[14px] mb-8 w-auto min-w-[160px] shadow-sm hover:bg-blue-600 transition-colors disabled:opacity-50"
          >
            {isCancelling ? (
              <FaSpinner className="animate-spin" />
            ) : (
              <div className="w-[10px] h-[10px] bg-white rounded-sm"></div>
            )}
            {isCancelling ? "Cancelling..." : "Pause Generating"}
          </button>
        )}
        {isCompleted && (
          <button
            onClick={() => {
              if (campaignUuid) {
                window.location.href = `/campaign/${campaignUuid}/emails`;
              }
            }}
            className="flex items-center justify-center gap-2 bg-[#10B981] text-white px-6 py-2.5 rounded-[8px] font-semibold text-[14px] mb-8 w-auto min-w-[160px] shadow-sm hover:bg-emerald-600 transition-colors"
          >
            View Generated Emails
          </button>
        )}
        {(isFailed || isCancelled) && (
          <button
            onClick={() => onOpenChange(false)}
            className="flex items-center justify-center gap-2 bg-gray-100 text-gray-700 px-6 py-2.5 rounded-[8px] font-semibold text-[14px] mb-8 w-auto min-w-[160px] shadow-sm hover:bg-gray-200 transition-colors"
          >
            Close
          </button>
        )}

        {/* Separator */}
        <div className="w-full h-px bg-gray-200 mb-6"></div>

        {/* Details List */}
        <div className="w-full space-y-4 mb-8">
          {/* NOT NEEDED: these are email sending fields */}
          {/* <div className="flex justify-between items-center text-[14px]">
            <span className="text-gray-500">Emails Processed</span>
            <span className="font-semibold text-[#111111]">
              {taskStatus?.sent_emails || 0}
            </span>
          </div>
          <div className="flex justify-between items-center text-[14px]">
            <span className="text-gray-500">Total Emails</span>
            <span className="font-semibold text-[#111111]">
              {taskStatus?.total_emails || 0}
            </span>
          </div> */}
          <div className="flex justify-between items-center text-[14px]">
            <span className="text-gray-500">Started</span>
            <span className="font-semibold text-[#111111]">
              {startTime
                ? startTime.toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                  })
                : "Waiting..."}
            </span>
          </div>
          <div className="flex justify-between items-center text-[14px]">
            <span className="text-gray-500">Time Elapsed</span>
            <span className="font-semibold text-[#111111]">
              {formatTime(elapsedTime)}
            </span>
          </div>
          <div className="flex justify-between items-center text-[14px]">
            <span className="text-gray-500">Estimated Time Remaining</span>
            <span className="font-semibold text-[#111111]">
              {isCompleted ? "Finished" : estimatedTime}
            </span>
          </div>
        </div>

        {/* Info Card */}
        {!isCompleted && (
          <div className="w-full bg-[#F9FAFB] border border-gray-100 rounded-[12px] p-5">
            <h4 className="text-[14px] font-semibold text-[#111111] mb-2">
              Background Processing
            </h4>
            <p className="text-[13px] text-gray-500 leading-relaxed ">
              Your emails are being generated in the background. You can safely
              navigate away from this page or even close your browser, and the
              generation process will continue.
            </p>
            {/* <p className="text-[13px] font-semibold text-[#111111] mb-2 mt-4">
              Bookmark this URL to check progress later:
            </p>
            <div className="bg-white px-3 py-2.5 rounded-[8px] border border-gray-100 break-all overflow-hidden text-ellipsis whitespace-nowrap text-[13px] text-[#006EFF]">
              {statusUrl ||
                (campaignUuid
                  ? `${window.location.origin}/campaign/${campaignUuid}/status`
                  : "No status URL available")}
            </div> */}
          </div>
        )}

        {/* Memory Management */}
        <div className="w-full bg-[#F9FAFB] border border-gray-100 rounded-[12px] p-5 mt-2">
          <h4 className="text-[14px] font-semibold text-[#111111] mb-2">
            Memory Management
          </h4>
          <p className="text-[13px] text-gray-500 leading-relaxed mb-4">
            To handle large batches efficiently, emails are processed in smaller
            groups to optimize memory usage.
          </p>
          <p className="text-[13px] text-gray-500 leading-relaxed mb-4">
            This approach allows the system to process thousands of leads
            without running out of resources.
          </p>
          <p className="text-[13px] text-gray-500 leading-relaxed mb-4">
            Partial results are saved after each batch, so even if there's an
            issue, you won't lose all progress.
          </p>
        </div>
      </div>

      <Dialog
        open={confirmModal.isOpen}
        onOpenChange={(open) =>
          setConfirmModal({ ...confirmModal, isOpen: open })
        }
      >
        <DialogContent
          className="max-w-md p-0 overflow-hidden border-none shadow-2xl"
          onClose={() => setConfirmModal({ ...confirmModal, isOpen: false })}
        >
          <div className="p-0">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-red-50 rounded-2xl flex items-center justify-center">
                <FaExclamationTriangle className="text-red-500 text-base" />
              </div>
              <div className="mb-0">
                <DialogTitle className="text-xl font-semibold text-[#17150b] leading-tight">
                  {confirmModal.title}
                </DialogTitle>
              </div>
            </div>

            <div className="space-y-4">
              <p className="text-[#7d7b78] leading-relaxed">
                {confirmModal.message}
              </p>
            </div>

            <div className="flex gap-3 mt-8">
              <button
                className="flex-1 px-4 py-3 bg-[#f8f8f8] hover:bg-gray-200 text-[#17150b] text-base font-semibold rounded-[10px] transition-all"
                onClick={() =>
                  setConfirmModal({ ...confirmModal, isOpen: false })
                }
              >
                Go Back
              </button>
              <button
                className="flex-1 px-4 py-3 bg-[#fef3f2] border border-[#ef4444] whitespace-nowrap text-[#ef4444] text-sm font-semibold rounded-[10px] transition-all flex items-center justify-center gap-2"
                onClick={confirmModal.onConfirm}
              >
                Yes, Cancel Task
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={notification.isOpen}
        onOpenChange={(open) =>
          setNotification({ ...notification, isOpen: open })
        }
      >
        <DialogContent
          className="max-w-md p-0 overflow-hidden border-none shadow-2xl"
          onClose={() => setNotification({ ...notification, isOpen: false })}
        >
          <div className="p-0">
            <div className="flex items-center gap-3 mb-6">
              <div
                className={`w-12 h-12 rounded-2xl flex items-center justify-center ${
                  notification.type === "error"
                    ? "bg-red-50"
                    : notification.type === "success"
                      ? "bg-green-50"
                      : "bg-blue-50"
                }`}
              >
                {notification.type === "error" ? (
                  <FaExclamationCircle className="text-red-500 text-base" />
                ) : notification.type === "success" ? (
                  <FaCheckCircle className="text-green-500 text-base" />
                ) : (
                  <FaInfoCircle className="text-blue-500 text-base" />
                )}
              </div>
              <div className="mb-0">
                <DialogTitle className="text-xl font-semibold text-[#17150b] leading-tight">
                  {notification.title}
                </DialogTitle>
              </div>
            </div>

            <div className="space-y-4">
              <p className="text-[#7d7b78] leading-relaxed text-base">
                {notification.message}
              </p>
            </div>

            <div className="flex gap-3 mt-8">
              <button
                className={`flex-1 px-4 py-3 text-white text-base font-semibold rounded-[10px] transition-all ${
                  notification.type === "error"
                    ? "bg-red-500 hover:bg-red-600"
                    : notification.type === "success"
                      ? "bg-green-500 hover:bg-green-600"
                      : "bg-brand-blue hover:bg-blue-600"
                }`}
                onClick={() =>
                  setNotification({ ...notification, isOpen: false })
                }
              >
                Got it
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Drawer>
  );
};
